import { createContext, useContext, useEffect, useRef, useState, ReactNode, FC } from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, Stack, Text } from '@fluentui/react';
import { HistoryFilled } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import Session from 'supertokens-web-js/recipe/session';
import { FluentProvider } from '@fluentui/react-components';
import { dashboardTheme } from '../../styles/customStyles';

interface iSessionContext {
  resetSession: () => void;
};

interface iSession {
  children: ReactNode;
};

const SessionContext = createContext<iSessionContext>({ resetSession: () => { } });

const SessionProvider: FC<iSession> = ({ children }) => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isLoggingOutRef = useRef(false);
  const navigate = useNavigate();

  const { Provider: Box } = SessionContext;
  const { normal } = DialogType;

  const resetSession = () => {
    if (isSessionExpired || isLoggingOutRef.current) return;
    lastActivityRef.current = Date.now();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    };
    const timeout = parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '1800000', 10);
    timeoutRef.current = setTimeout(() => {
      const currentTime = Date.now();
      const timeSinceLastActivity = currentTime - lastActivityRef.current;
      if (timeSinceLastActivity >= timeout) {
        setIsSessionExpired(true);
      };
    }, timeout);
  };

  const checkSessionStatus = async () => {
    try {
      const checks = await Session.doesSessionExist();
      if (!checks && !isLoggingOutRef.current) {
        handleLogout();
      };
    } catch (error) {
      console.error("Error checking session:", error);
      handleLogout();
    };
  };

  useEffect(() => {
    const handleActivity = () => {
      if (!isSessionExpired && !isLoggingOutRef.current) {
        resetSession();
      };
    };
    const inputEvents = ['mousemove', 'keypress', 'mousedown', 'touchstart', 'scroll'];
    // Add all activity event listeners
    inputEvents.forEach(event => { window.addEventListener(event, handleActivity, { passive: true }) });
    // Initial session check and reset
    checkSessionStatus();
    resetSession();
    // Set up periodic session checks
    const interval = setInterval(checkSessionStatus, 60000); // Interval
    return () => {
      // Clean up all event listeners
      inputEvents.forEach(event => { window.removeEventListener(event, handleActivity) });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      };
      clearInterval(interval);
    };
  }, [isSessionExpired]);

  const handleLogout = async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    try {
      await Session.signOut();
      setIsSessionExpired(false);
      navigate('/');
    } catch (error) {
      console.error("Failed to logout:", error);
      navigate('/'); // Even if logout fails, we should still redirect to login
    } finally {
      isLoggingOutRef.current = false;
    };
  };

  return (
    <Box value={{ resetSession }}>
      {children}
      <FluentProvider theme={dashboardTheme}>
        <Dialog hidden={!isSessionExpired} onDismiss={handleLogout} dialogContentProps={{
          type: normal,
          title: <Stack tokens={{ childrenGap: 5 }} horizontal> <HistoryFilled /> <Text> Session Expired </Text> </Stack>,
          subText: 'Your session has expired due to inactivity. You will be redirected to the login page.',
        }}>
          <DialogFooter> <PrimaryButton shape="circular" onClick={handleLogout} text="OK" /> </DialogFooter>
        </Dialog>
      </FluentProvider>
    </Box>
  );
};

export const useSession = () => useContext(SessionContext);

export default SessionProvider;
